import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Action,
  Actions,
  NgxsOnInit,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, debounceTime, of, Subject, tap } from 'rxjs';
import {
  EventTypeToHeader,
  incidentEventActorName,
} from 'src/app/models/events';
import {
  IncidentSchemaPriority,
  IncidentSchemaState,
} from 'src/app/models/incident-schema';
import {
  IncidentActivityChange,
  IncidentActivityToastComponent,
} from 'src/app/pages/internal/incidents/components/incident-activity-toast/incident-activity-toast.component';
import {
  IncidentEvent,
  IncidentEventPayloadPriorityUpdated,
  IncidentEventPayloadStateUpdated,
} from 'src/app/models/incident';
import { CLIENT_INSTANCE_ID } from 'src/app/services/realtime/client-instance';
import {
  ToastExtras,
  ToastService,
} from 'src/app/services/toast/toast.service';
import {
  AddIncidentEventAction,
  CreateIncidentAction,
  DeleteIncidentAction,
  UpdateIncidentStateAction,
  UpdateSelectedIncidentState,
  UpdateSelectedIncidentTiming,
} from 'src/app/store/incidents/incidents.actions';
import { RealtimeReconnectedAction } from 'src/app/store/misc/misc.actions';
import { UserState } from 'src/app/store/user/user.state';
import {
  ActiveIncidentCountChanged,
  LoadActiveIncidentCount,
} from './incident-activity.actions';
import {
  IncidentActivityService,
  IncidentToastSummary,
} from './incident-activity.service';
import {
  IncidentToastSnoozeDuration,
  IncidentToastSnoozeService,
} from './incident-toast-snooze.service';

export interface IncidentActivityStateModel {
  /** Unresolved incidents in the current organization; null until the first
   *  successful load, so consumers can distinguish "0" from "unknown". */
  activeIncidentCount: number | null;
}

/** Coalesces bursts (a mutation plus its own realtime echo) into one count. */
const COUNT_REFRESH_DEBOUNCE_MS = 300;

/** Toasts about incidents you're not looking at carry an Open action, which
 *  would otherwise make them sticky. */
const OTHER_INCIDENT_TOAST_MS = 10_000;

/** Header per event type toasted for incidents other than the open one. */
const OTHER_INCIDENT_HEADERS: Partial<Record<string, string>> = {
  CREATED: 'incidentEvents.activity.created',
  STATE_UPDATED: 'incidentEvents.activity.stateUpdated',
  PRIORITY_UPDATED: 'incidentEvents.activity.priorityUpdated',
};

/** Snooze menu entries; labels are translation keys. */
const SNOOZE_OPTIONS: {
  duration: IncidentToastSnoozeDuration;
  label: string;
}[] = [
  { duration: '15m', label: 'incidentEvents.snooze.options.m15' },
  { duration: '1h', label: 'incidentEvents.snooze.options.h1' },
  { duration: '4h', label: 'incidentEvents.snooze.options.h4' },
  { duration: 'tomorrow', label: 'incidentEvents.snooze.options.tomorrow' },
];

/** `/incidents/123` and its tabs — the incident whose page is open. */
const INCIDENT_DETAILS_URL = /^\/incidents\/(\d+)(?:[/?#]|$)/;

/**
 * App-wide incident activity, registered at root (unlike the page-scoped
 * `IncidentsState`). Consumes the incident event stream (`RealtimeService`
 * dispatches `AddIncidentEventAction` wherever the user is) to keep the
 * sidebar's active incident count fresh and to toast incident activity:
 * compact for the incident whose page is open, with an Open action for any
 * other incident. A change isn't toasted in the tab whose request made it
 * (matched by `CLIENT_INSTANCE_ID`) — the same user's changes from another
 * window or device are — nor while its incident (or all incidents) is
 * snoozed from a toast's menu.
 *
 * The count also re-fetches after this user's mutations (so it stays right
 * while the socket is down), on app start and after a realtime reconnect.
 */
@State<IncidentActivityStateModel>({
  name: 'incidentActivity',
  defaults: {
    activeIncidentCount: null,
  },
})
@Injectable()
export class IncidentActivityState implements NgxsOnInit {
  private store = inject(Store);
  private actions = inject(Actions);
  private destroyRef = inject(DestroyRef);
  private incidentActivityService = inject(IncidentActivityService);
  private toast = inject(ToastService);
  private clientInstanceId = inject(CLIENT_INSTANCE_ID);
  private snooze = inject(IncidentToastSnoozeService);
  private transloco = inject(TranslocoService);
  private router = inject(Router);

  private readonly countRefresh$ = new Subject<void>();

  @Selector()
  static activeIncidentCount(state: IncidentActivityStateModel) {
    return state.activeIncidentCount;
  }

  ngxsOnInit(ctx: StateContext<IncidentActivityStateModel>) {
    this.initCountRefresh(ctx);
    this.initMutationRefresh();
  }

  private initCountRefresh(ctx: StateContext<IncidentActivityStateModel>) {
    this.countRefresh$
      .pipe(
        debounceTime(COUNT_REFRESH_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => ctx.dispatch(new LoadActiveIncidentCount()));
  }

  /** Re-count after any of this user's mutations that change active-ness:
   *  creation, deletion, state changes (list page + details page) and impact
   *  timings (which include `resolvedAt`). */
  private initMutationRefresh() {
    this.actions
      .pipe(
        ofActionSuccessful(
          CreateIncidentAction,
          DeleteIncidentAction,
          UpdateIncidentStateAction,
          UpdateSelectedIncidentState,
          UpdateSelectedIncidentTiming
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.countRefresh$.next());
  }

  @Action(AddIncidentEventAction)
  incidentEventReceived(
    _ctx: StateContext<IncidentActivityStateModel>,
    { incidentId, event }: AddIncidentEventAction
  ) {
    if (!this.canReadIncidents()) {
      return;
    }

    if (this.changesActiveCount(event)) {
      this.countRefresh$.next();
    }

    if (this.isLocalEcho(event) || this.snooze.isSnoozed(incidentId)) {
      return;
    }

    if (incidentId === this.openIncidentId()) {
      this.toastOpenIncidentEvent(incidentId, event);
    } else {
      this.toastOtherIncidentEvent(incidentId, event);
    }
  }

  @Action(RealtimeReconnectedAction)
  resyncAfterReconnect() {
    this.countRefresh$.next();
  }

  private changesActiveCount(event: IncidentEvent): boolean {
    switch (event.type) {
      case 'CREATED':
      case 'DELETED':
        return true;
      case 'STATE_UPDATED': {
        const { oldState, newState } =
          event.payload as IncidentEventPayloadStateUpdated;
        return oldState.isResolved !== newState.isResolved;
      }
      default:
        return false;
    }
  }

  /** The timeline already shows it — a short "<actor> <did what>" suffices. */
  private toastOpenIncidentEvent(incidentId: number, event: IncidentEvent) {
    const action = EventTypeToHeader(event);
    if (!action) {
      return;
    }

    this.toast.showInfo(
      'incidentEvents.toastHeader',
      'incidentEvents.activity.openIncident',
      { actor: incidentEventActorName(event), action: this.translate(action) },
      { menu: this.snoozeMenu(incidentId) }
    );
  }

  /** Only the events worth interrupting for. Events carry just the
   *  incident's number and the changed field, so title, current state and
   *  the schema's states are fetched for the body. */
  private toastOtherIncidentEvent(incidentId: number, event: IncidentEvent) {
    const headerKey = OTHER_INCIDENT_HEADERS[event.type];
    if (!headerKey) {
      return;
    }

    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      return;
    }

    this.incidentActivityService
      .incidentToastSummary(organizationID, incidentId)
      // without the summary the body still shows the change from the event
      .pipe(catchError(() => of(undefined)))
      .subscribe(summary =>
        this.toast.showInfo(
          headerKey,
          undefined,
          { actor: incidentEventActorName(event) },
          {
            ...this.otherIncidentLook(event),
            content: {
              component: IncidentActivityToastComponent,
              inputs: {
                incidentId,
                incidentTitle: summary?.title,
                change: this.activityChange(event, summary),
                states: summary?.states ?? [],
              },
            },
            hint: 'incidentEvents.activity.by',
            menu: this.snoozeMenu(incidentId),
            action: {
              label: 'incidentEvents.activity.open',
              handler: () => this.router.navigate(['/incidents', incidentId]),
            },
            duration: OTHER_INCIDENT_TOAST_MS,
          }
        )
      );
  }

  /** Tile tone + glyph: a new incident asks for attention, a resolution
   *  reads as good news, anything else stays neutral. */
  private otherIncidentLook(
    event: IncidentEvent
  ): Pick<ToastExtras, 'tone' | 'icon'> {
    switch (event.type) {
      case 'CREATED':
        return { tone: 'warning', icon: 'fire' };
      case 'STATE_UPDATED': {
        const { newState } = event.payload as IncidentEventPayloadStateUpdated;
        return newState.isResolved
          ? { tone: 'success', icon: 'check-circle' }
          : { icon: 'arrows-clockwise' };
      }
      case 'PRIORITY_UPDATED':
        return { icon: 'cell-signal-full' };
      default:
        return {};
    }
  }

  /** "Snooze" in the toast's corner: this incident, or every incident. */
  private snoozeMenu(incidentId: number): ToastExtras['menu'] {
    const items = (snooze: (duration: IncidentToastSnoozeDuration) => void) =>
      SNOOZE_OPTIONS.map(({ duration, label }) => ({
        label,
        handler: () => snooze(duration),
      }));

    return {
      label: 'incidentEvents.snooze.label',
      icon: 'bell-slash',
      groups: [
        {
          label: 'incidentEvents.snooze.thisIncident',
          items: items(duration =>
            this.snooze.snoozeIncident(incidentId, duration)
          ),
        },
        {
          label: 'incidentEvents.snooze.allIncidents',
          items: items(duration => this.snooze.snoozeAll(duration)),
        },
      ],
    };
  }

  /** Event payloads carry trimmed state/priority objects; prefer the
   *  schema's full ones (with `order`, for the progress arc) when known. */
  private activityChange(
    event: IncidentEvent,
    summary?: IncidentToastSummary
  ): IncidentActivityChange {
    switch (event.type) {
      case 'STATE_UPDATED': {
        const { oldState, newState } =
          event.payload as IncidentEventPayloadStateUpdated;
        const full = (state: IncidentSchemaState) =>
          summary?.states.find(candidate => candidate.id === state.id) ?? state;
        return { kind: 'state', from: full(oldState), to: full(newState) };
      }
      case 'PRIORITY_UPDATED': {
        const { oldPriority, newPriority } =
          event.payload as IncidentEventPayloadPriorityUpdated;
        const full = (priority: IncidentSchemaPriority) =>
          summary?.priorities.find(candidate => candidate.id === priority.id) ??
          priority;
        return {
          kind: 'priority',
          from: full(oldPriority),
          to: full(newPriority),
        };
      }
      default:
        return {
          kind: 'created',
          state: summary?.state,
          priority: summary?.priority,
        };
    }
  }

  /** A request from this tab caused the event — the page that made the
   *  change already gave feedback. */
  private isLocalEcho(event: IncidentEvent): boolean {
    return event.clientInstanceId === this.clientInstanceId;
  }

  private openIncidentId(): number | undefined {
    const match = INCIDENT_DETAILS_URL.exec(this.router.url);
    return match ? Number(match[1]) : undefined;
  }

  private canReadIncidents(): boolean {
    try {
      return this.store.selectSnapshot(
        UserState.checkPermissionRole(['incidents.reader'])
      );
    } catch {
      // membership not loaded yet
      return false;
    }
  }

  private translate(key: string): string {
    return this.transloco.translate(key);
  }

  @Action(LoadActiveIncidentCount)
  loadActiveIncidentCount(ctx: StateContext<IncidentActivityStateModel>) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );

    // Mutation-triggered refreshes can race logout; a badge count is not
    // worth an error toast, so missing org/network failures degrade to
    // keeping the last known count.
    if (!organizationID) {
      return;
    }

    return this.incidentActivityService
      .activeIncidentCount(organizationID)
      .pipe(
        tap(count => ctx.dispatch(new ActiveIncidentCountChanged(count))),
        catchError(() => of(undefined))
      );
  }

  @Action(ActiveIncidentCountChanged)
  activeIncidentCountChanged(
    ctx: StateContext<IncidentActivityStateModel>,
    { count }: ActiveIncidentCountChanged
  ) {
    ctx.patchState({ activeIncidentCount: count });
  }
}
